import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Button } from '@components';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';

import {
    projectGetByIdAction,
    projectUpdateAction
} from '@app/store/reducers/project';
import { quyTrinhGetAllAction } from '@app/store/reducers/quyTrinh';
import { teamGetAllAction } from '@app/store/reducers/team';
import { URI_INTERNAL } from '@app/utils/pathLocations';
import { toast } from 'react-toastify';
import { messageToast } from '@app/utils/define';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function toInputDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

const ProjectEdit = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const query = useQuery();

    const idParam = query.get('id');
    const id = idParam ? parseInt(idParam, 10) : NaN;

    const { item } = useSelector((state) => state.project);
    const { list: quyTrinhList } = useSelector((state) => state.quyTrinh);
    const { list: teamList } = useSelector((state) => state.team);

    const safeReturnUrl = useMemo(() => {
        const raw = query.get('returnUrl') || '';
        if (!raw) return '';
        let decoded = raw;
        try { decoded = decodeURIComponent(raw); } catch { /* ignore */ }
        if (decoded.startsWith('/')) return decoded;
        return '';
    }, [location.search]);

    const [name, setName] = useState('');
    const [moTa, setMoTa] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [actualEndDate, setActualEndDate] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [selectedQuyTrinhIds, setSelectedQuyTrinhIds] = useState([]);
    const [checkAllQuyTrinh, setCheckAllQuyTrinh] = useState(false);

    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [checkAllTeams, setCheckAllTeams] = useState(false);

    const [initialized, setInitialized] = useState(false);

    const [existingFiles, setExistingFiles] = useState([]);
    const [filesMarkedForDelete, setFilesMarkedForDelete] = useState([]);
    const [newFiles, setNewFiles] = useState([]);

    const newFileInputRef = useRef(null);

    const toFileKey = (f) => `${f.name}__${f.size}__${f.lastModified}`;

    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return '';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        let v = bytes;
        let i = 0;
        while (v >= 1024 && i < units.length - 1) {
            v = v / 1024;
            i += 1;
        }
        return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    };

    const smallBtnStyle = { padding: '2px 10px', fontSize: 12, lineHeight: '18px' };

    useEffect(() => {
        if (!Number.isNaN(id)) {
            dispatch(projectGetByIdAction(id));
        }
        dispatch(quyTrinhGetAllAction());
        dispatch(teamGetAllAction());
    }, [dispatch, id]);

    const orderedQuyTrinhList = useMemo(() => {
        if (!Array.isArray(quyTrinhList)) return [];
        return [...quyTrinhList].sort(
            (a, b) => (a.thuTu || 0) - (b.thuTu || 0)
        );
    }, [quyTrinhList]);

    const orderedTeamList = useMemo(() => {
        if (!Array.isArray(teamList)) return [];
        return [...teamList].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'vi', {
                sensitivity: 'base'
            })
        );
    }, [teamList]);

    useEffect(() => {
        if (!item || Number.isNaN(id) || item.id !== id || initialized) return;

        setName(item.name || '');
        setMoTa(item.moTa || '');
        setStartDate(toInputDate(item.startDate));
        setEndDate(toInputDate(item.endDate));
        setActualEndDate(toInputDate(item.actualEndDate));

        const stepIds = (item.steps || []).map((s) => s.quyTrinhId);
        const teamIds = (item.teams || []).map((t) => t.teamId);
        const files = Array.isArray(item.files) ? item.files : [];

        setSelectedQuyTrinhIds(stepIds);
        setSelectedTeamIds(teamIds);
        setExistingFiles(files);

        setInitialized(true);
    }, [item, id, initialized]);

    useEffect(() => {
        if (!initialized) return;

        const allStepIds = orderedQuyTrinhList.map((x) => x.id);
        setCheckAllQuyTrinh(
            allStepIds.length > 0 &&
            allStepIds.every((x) => selectedQuyTrinhIds.includes(x))
        );

        const allTeamIds = orderedTeamList.map((x) => x.id);
        setCheckAllTeams(
            allTeamIds.length > 0 &&
            allTeamIds.every((x) => selectedTeamIds.includes(x))
        );
    }, [
        initialized,
        orderedQuyTrinhList,
        orderedTeamList,
        selectedQuyTrinhIds,
        selectedTeamIds
    ]);

    const handleToggleQuyTrinh = (idQuyTrinh) => {
        let newIds = [];
        if (selectedQuyTrinhIds.includes(idQuyTrinh)) {
            newIds = selectedQuyTrinhIds.filter((x) => x !== idQuyTrinh);
        } else {
            newIds = [...selectedQuyTrinhIds, idQuyTrinh];
        }
        setSelectedQuyTrinhIds(newIds);
    };

    const handleToggleAllQuyTrinh = () => {
        const allIds = orderedQuyTrinhList.map((x) => x.id);
        const newValue = !checkAllQuyTrinh;
        setCheckAllQuyTrinh(newValue);
        setSelectedQuyTrinhIds(newValue ? allIds : []);
    };

    const handleToggleTeam = (teamId) => {
        let newIds = [];
        if (selectedTeamIds.includes(teamId)) {
            newIds = selectedTeamIds.filter((x) => x !== teamId);
        } else {
            newIds = [...selectedTeamIds, teamId];
        }
        setSelectedTeamIds(newIds);
    };

    const handleToggleAllTeams = () => {
        const allIds = orderedTeamList.map((x) => x.id);
        const newValue = !checkAllTeams;
        setCheckAllTeams(newValue);
        setSelectedTeamIds(newValue ? allIds : []);
    };

    const handleExistingFileToggle = (fileId) => {
        setFilesMarkedForDelete((prev) => {
            if (prev.includes(fileId)) {
                return prev.filter((x) => x !== fileId);
            }
            return [...prev, fileId];
        });
    };

    const handleNewFilesChange = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length === 0) return;

        setNewFiles((prev) => {
            const map = new Map(prev.map((f) => [toFileKey(f), f]));
            picked.forEach((f) => map.set(toFileKey(f), f));
            return Array.from(map.values());
        });

        if (newFileInputRef.current) {
            newFileInputRef.current.value = '';
        }
    };

    const handleRemoveNewFile = (index) => {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleClearNewFiles = () => {
        setNewFiles([]);
        if (newFileInputRef.current) {
            newFileInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (Number.isNaN(id)) {
            toast.error('Thiếu id dự án');
            return;
        }

        if (!name.trim()) {
            toast.error('Vui lòng nhập tên dự án');
            return;
        }

        if (startDate && endDate) {
            const sd = new Date(startDate);
            const ed = new Date(endDate);
            if (ed < sd) {
                toast.error('Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu');
                return;
            }
        }

        if (startDate && actualEndDate) {
            const sd = new Date(startDate);
            const ad = new Date(actualEndDate);
            if (ad < sd) {
                toast.error('Ngày hoàn thành thực tế phải lớn hơn hoặc bằng ngày bắt đầu');
                return;
            }
        }

        const actual = actualEndDate || null;

        const model = {
            name: name.trim(),
            moTa: moTa ? moTa.trim() : null,
            startDate: startDate || null,
            endDate: endDate || null,
            actualEndDate: actual,
            quyTrinhIds: selectedQuyTrinhIds,
            teamIds: selectedTeamIds,
            files: newFiles,
            removeFileIds: filesMarkedForDelete,
        };
        
        try {
            setSubmitting(true);
            await dispatch(projectUpdateAction({ id, data: model })).unwrap();

            toast.success(messageToast.messageToastUpdateSuccess);
            history.push(safeReturnUrl || URI_INTERNAL.PROJECT_PATH);
        } catch (err) {
            console.error('Cập nhật dự án lỗi:', err);

            let msg =
                (err && err.message) ||
                (typeof err === 'string' ? err : '') ||
                (err && err.title) ||
                '';

            if (!msg && err && err.errors) {
                msg = Object.values(err.errors).flat().join('; ');
            }

            toast.error(
                msg
                    ? `${messageToast.messageToastUpdateError}: ${msg}`
                    : messageToast.messageToastUpdateError
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (Number.isNaN(id)) return <p>Thiếu id</p>;

    return (
        <Card>
            <Card.Header as="h5">Sửa dự án</Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit} noValidate>
                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Tên dự án <span className="text-danger">*</span>
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                type="text"
                                placeholder="Nhập tên dự án"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Ngày bắt đầu
                        </Form.Label>
                        <Col sm="4">
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Col>
                        <Form.Label className="col-sm-2 col-form-label">
                            Ngày kết thúc dự kiến
                        </Form.Label>
                        <Col sm="4">
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Ngày hoàn thành thực tế
                        </Form.Label>
                        <Col sm="4">
                            <Form.Control
                                type="date"
                                value={actualEndDate}
                                onChange={(e) => setActualEndDate(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Mô tả
                        </Form.Label>
                        <Col sm="10">
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Mô tả ngắn gọn về dự án"
                                value={moTa}
                                onChange={(e) => setMoTa(e.target.value)}
                            />
                        </Col>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                                Chọn lại các bước quy trình (Phát triển phần
                                mềm)
                            </h5>
                            <div>
                                <input
                                    type="checkbox"
                                    id="checkAllSteps"
                                    checked={checkAllQuyTrinh}
                                    onChange={handleToggleAllQuyTrinh}
                                />{' '}
                                <label htmlFor="checkAllSteps">
                                    Chọn tất cả
                                </label>
                            </div>
                        </div>
                        <Row>
                            {orderedQuyTrinhList.map((q) => (
                                <Col md={4} className="mb-2" key={q.id}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`quytrinh_${q.id}`}
                                        label={
                                            <>
                                                {q.name}{' '}
                                                {typeof q.thuTu === 'number'
                                                    ? `(Bước ${q.thuTu})`
                                                    : ''}
                                            </>
                                        }
                                        checked={selectedQuyTrinhIds.includes(
                                            q.id
                                        )}
                                        onChange={() =>
                                            handleToggleQuyTrinh(q.id)
                                        }
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                                Chọn nhóm thực hiện dự án
                            </h5>
                            <div>
                                <input
                                    type="checkbox"
                                    id="checkAllTeams"
                                    checked={checkAllTeams}
                                    onChange={handleToggleAllTeams}
                                />{' '}
                                <label htmlFor="checkAllTeams">
                                    Chọn tất cả
                                </label>
                            </div>
                        </div>
                        <Row>
                            {orderedTeamList.map((t) => (
                                <Col md={4} className="mb-2" key={t.id}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`team_${t.id}`}
                                        label={t.name}
                                        checked={selectedTeamIds.includes(
                                            t.id
                                        )}
                                        onChange={() => handleToggleTeam(t.id)}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Tệp hiện tại
                        </Form.Label>

                        <Col sm="10">
                            {existingFiles.length > 0 && (
                                <Row className="mt-1">
                                    <Col md={6}>
                                        <div className="border rounded">
                                            <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                                                <div className="text-muted">
                                                    Có {existingFiles.length} tệp
                                                    {filesMarkedForDelete.length > 0
                                                        ? ` (đã chọn xóa ${filesMarkedForDelete.length})`
                                                        : ''}
                                                </div>

                                                {filesMarkedForDelete.length > 0 && (
                                                    <Button
                                                        type="button"
                                                        theme="soft"
                                                        size="sm"
                                                        onClick={() => setFilesMarkedForDelete([])}
                                                        disabled={submitting}
                                                    >
                                                        Bỏ chọn xóa
                                                    </Button>
                                                )}
                                            </div>

                                            <div className="px-2 pb-1">
                                                {existingFiles.map((f, idx) => {
                                                    const marked = filesMarkedForDelete.includes(f.id);

                                                    return (
                                                        <div
                                                            key={f.id}
                                                            className={`d-flex justify-content-between align-items-center py-2 ${
                                                                idx < existingFiles.length - 1 ? 'border-bottom' : ''
                                                            }`}
                                                        >
                                                            <div className="me-3" style={{ minWidth: 0 }}>
                                                                <div
                                                                    className={`fw-semibold text-truncate ${marked ? 'text-muted' : ''}`}
                                                                    title={f.name || 'Tệp đính kèm'}
                                                                >
                                                                    {f.name || 'Tệp đính kèm'}
                                                                </div>
                                                                {marked && (
                                                                    <div className="text-muted small">
                                                                        Sẽ xóa khi bấm Lưu
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <Button
                                                                type="button"
                                                                theme={marked ? 'secondary' : 'danger'}
                                                                size="sm"
                                                                style={smallBtnStyle}
                                                                onClick={() => handleExistingFileToggle(f.id)}
                                                                disabled={submitting}
                                                            >
                                                                {marked ? 'Hoàn tác' : 'Xóa'}
                                                            </Button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Form.Label className="col-sm-2 col-form-label">
                            Thêm tệp mới
                        </Form.Label>

                        <Col sm="10">
                            <Form.Control
                                ref={newFileInputRef}
                                type="file"
                                multiple
                                onChange={handleNewFilesChange}
                                disabled={submitting}
                            />

                            {newFiles.length > 0 && (
                                <Row className="mt-2">
                                    <Col md={6}>
                                        <div className="border rounded">
                                            <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
                                                <div className="text-muted">
                                                    Đã chọn {newFiles.length} file
                                                </div>
                                                <Button
                                                    type="button"
                                                    theme="soft"
                                                    size="sm"
                                                    onClick={handleClearNewFiles}
                                                    disabled={submitting}
                                                >
                                                    Bỏ tất cả
                                                </Button>
                                            </div>

                                            <div className="px-2 pb-1">
                                                {newFiles.map((f, idx) => (
                                                    <div
                                                        key={toFileKey(f)}
                                                        className={`d-flex justify-content-between align-items-center py-2 ${
                                                            idx < newFiles.length - 1 ? 'border-bottom' : ''
                                                        }`}
                                                    >
                                                        <div className="me-3" style={{ minWidth: 0 }}>
                                                            <div className="fw-semibold text-truncate" title={f.name}>
                                                                {f.name}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {formatBytes(f.size)}
                                                            </div>
                                                        </div>

                                                        <Button
                                                            type="button"
                                                            theme="danger"
                                                            size="sm"
                                                            style={smallBtnStyle}
                                                            onClick={() => handleRemoveNewFile(idx)}
                                                            disabled={submitting}
                                                        >
                                                            Xóa
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Form.Group>

                    <Form.Group as={Row} className="mb-3">
                        <Col md={{ span: 10, offset: 2 }}>
                            <ButtonGroup className="mb-2">
                                <Button
                                    type="submit"
                                    theme="primary"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Đang lưu...' : 'Lưu'}
                                </Button>
                                &nbsp;&nbsp;&nbsp;
                                <Button
                                    type="button"
                                    theme="secondary"
                                    onClick={() =>
                                        history.push(safeReturnUrl || URI_INTERNAL.PROJECT_PATH)
                                    }
                                    disabled={submitting}
                                >
                                    Hủy
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Form.Group>
                </Form>

                <ReactSpinner loadings={submitting} />
            </Card.Body>
        </Card>
    );
};

export default ProjectEdit;
